// locationService.ts - Location lookup service for frontend

interface Address {
    line1?: string;
    line2?: string;
    city?: string;
    state?: string;
    country?: string;
    postcode: string;
}

interface LocationData {
    errored: boolean;
    latitude: number | null;
    longitude: number | null;
    postcode?: string;
    region?: string;
    outcode?: string;
}

async function getUKLocationDataWithPostcode(postcode: string): Promise<LocationData> {
    try {
        const response = await fetch(`https://api.postcodes.io/postcodes/${encodeURIComponent(postcode)}`);

        if (!response.ok) {
            if (response.status === 404) {
                console.warn(`Postcode not found: ${postcode}`);
                return {
                    errored: true,
                    latitude: null,
                    longitude: null
                };
            }
            throw new Error(`Failed to fetch postcode data, status: ${response.status}`);
        }

        const data = await response.json();
        if (!data.result) {
            console.warn(`Postcode result empty for: ${postcode}`);
            return {
                errored: true,
                latitude: null,
                longitude: null
            };
        }
        
        return {
            errored: false,
            postcode: data.result.postcode,
            latitude: data.result.latitude,
            longitude: data.result.longitude,
            region: data.result.region,
            outcode: data.result.outcode
        };

    } catch (error: any) {
        console.error("Error fetching postcode data:", error);
        return {
            errored: true,
            postcode: undefined,
            latitude: null,
            longitude: null,
            region: undefined,
            outcode: undefined
        };
    }
}

async function getUSLocationDataWithZipCode(zipCode: string): Promise<LocationData> {
    try {
        // Use HTTPS instead of HTTP to avoid mixed content issues
        const response = await fetch(`https://api.zippopotam.us/us/${encodeURIComponent(zipCode)}`);

        if (!response.ok) {
            if (response.status === 404) {
                console.warn(`ZIP code not found: ${zipCode}`);
                return {
                    errored: true,
                    latitude: null,
                    longitude: null
                };
            }
            throw new Error(`Failed to fetch ZIP code data, status: ${response.status}`);
        }

        const data = await response.json();
        if (!data.places || data.places.length === 0) {
            console.warn(`ZIP code result empty for: ${zipCode}`);
            return {
                errored: true,
                latitude: null,
                longitude: null
            };
        }
        
        // Use the first place if multiple results
        const place = data.places[0];
        return {
            errored: false,
            latitude: parseFloat(place.latitude),
            longitude: parseFloat(place.longitude)
        };

    } catch (error: any) {
        console.error("Error fetching ZIP code data:", error);
        return {
            errored: true,
            latitude: null,
            longitude: null
        };
    }
}

// Helper function to detect country from address or context
function detectCountryFromAddress(address: Address, countryCode?: string): 'US' | 'UK' | 'OTHER' {
    if (countryCode) {
        if (countryCode === 'US') return 'US';
        if (countryCode === 'GB' || countryCode === 'UK') return 'UK';
    }
    
    // Try to detect from postal code patterns
    if (address.postcode) {
        // US ZIP code pattern
        if (/^\d{5}(-\d{4})?$/.test(address.postcode.trim())) {
            return 'US';
        }
        // UK postcode pattern (basic)
        if (/^[A-Z]{1,2}[0-9][A-Z0-9]?\s?[0-9][A-Z]{2}$/i.test(address.postcode.trim())) {
            return 'UK';
        }
    }
    
    return 'OTHER';
}

async function tryNominatimStructuredQuery(addressParts: string[], countryCode?: string): Promise<LocationData> {
    try {
        // Convert address parts to Address object for country detection
        const tempAddress: Address = {
            line1: addressParts[0]?.trim(),
            city: addressParts[1]?.trim(),
            state: addressParts[2]?.trim(),
            postcode: addressParts[3]?.trim() || addressParts[2]?.match(/\d{5}(-\d{4})?$/)?.[0] || '',
            country: countryCode
        };
        
        const detectedCountry = detectCountryFromAddress(tempAddress, countryCode);
        console.log('Detected country format:', detectedCountry);
        
        let structuredQueries: any[] = [];
        
        if (detectedCountry === 'US') {
            // US address format: [street, city, state, zip] or [street, city, "state zip"]
            console.log("Address Parts: ", addressParts)
            const street = addressParts[0]?.trim();
            const city = addressParts[1]?.trim();
            const stateAndZip = addressParts[2]?.trim();
            
            // Try to separate state and zip
            let state = stateAndZip;
            let postalcode = '';
            
            if (stateAndZip) {
                const zipMatch = stateAndZip.match(/^(.+?)\s*(\d{5}(-\d{4})?)$/);
                if (zipMatch) {
                    state = zipMatch[1].trim();
                    postalcode = zipMatch[2];
                }
            }
            
            // If we have a separate zip in the 4th position
            if (addressParts[3]) {
                postalcode = addressParts[3].trim();
            }


            console.log("postalcode", postalcode)

            structuredQueries = [
                {
                    street: street,
                    // city: city,
                    // state: state,
                    postalcode: postalcode,
                    country: 'US'
                },
                {
                    street: street,
                    // city: city,
                    // state: state,
                    postalcode: postalcode,
                    country: 'US'
                },
                {
                    street: street,
                    city: city,
                    postalcode: postalcode,
                    country: 'US'
                }
            ];
            
        } else if (detectedCountry === 'UK') {
            // UK address format: [street, city, postcode] or variations
            const street = addressParts[0]?.trim();
            const city = addressParts[1]?.trim();
            let postalcode = addressParts[2]?.trim();
            
            // UK addresses might have county, but we'll treat it as part of city or ignore
            if (addressParts.length > 3) {
                // If 4 parts, might be [street, city, county, postcode]
                postalcode = addressParts[addressParts.length - 1]?.trim();
            }

            structuredQueries = [
                {
                    street: street,
                    city: city,
                    postalcode: postalcode,
                    country: 'GB'
                },
                {
                    street: street,
                    city: city,
                    country: 'GB'
                }
            ];
            
        } else {
            // Generic international format
            const street = addressParts[0]?.trim();
            const city = addressParts[1]?.trim();
            const postalcode = addressParts[addressParts.length - 1]?.trim();

            structuredQueries = [
                {
                    street: street,
                    city: city,
                    postalcode: postalcode
                },
                {
                    street: street,
                    city: city
                }
            ];
        }

        // Filter out queries that don't have minimum required fields
        // structuredQueries = structuredQueries.filter(query => query.street && query.city);

        for (let i = 0; i < structuredQueries.length; i++) {
            const query = structuredQueries[i];
            console.log(`Trying Nominatim structured query ${i + 1}:`, query);
            
            const params = new URLSearchParams();
            if (query.street) params.append('street', query.street);
            if (query.city) params.append('city', query.city);
            if (query.state) params.append('state', query.state);
            if (query.postalcode) params.append('postalcode', query.postalcode);
            if (query.country) params.append('country', query.country);
            params.append('format', 'json');
            params.append('addressdetails', '1');
            params.append('limit', '1');

            const url = `https://nominatim.openstreetmap.org/search?${params.toString()}`;
            console.log('Structured Nominatim URL:', url);

            const response = await fetch(url, {
                headers: {
                    'User-Agent': 'DinePlatform/1.0',
                    'Accept': 'application/json',
                    'Accept-Language': 'en'
                }
            });

            console.log(`Structured query ${i + 1} response status:`, response.status);

            if (!response.ok) {
                console.warn(`Structured query ${i + 1} failed with status ${response.status}, trying next...`);
                continue;
            }

            const data = await response.json();
            console.log(`Structured query ${i + 1} response data:`, data);

            if (data && data.length > 0) {
                const result = data[0];
                console.log('Successfully found location with structured query:', query);
                console.log('Using structured nominatim result:', result);
                return {
                    errored: false,
                    latitude: parseFloat(result.lat),
                    longitude: parseFloat(result.lon)
                };
            } else {
                console.warn(`Structured query ${i + 1} returned empty results, trying next...`);
            }

            // Add a small delay between requests
            if (i < structuredQueries.length - 1) {
                await new Promise(resolve => setTimeout(resolve, 100));
            }
        }

        console.warn('All structured queries failed');
        return {
            errored: true,
            latitude: null,
            longitude: null
        };

    } catch (error: any) {
        console.error("Error with structured nominatim query:", error);
        return {
            errored: true,
            latitude: null,
            longitude: null
        };
    }
}

async function getLocationDataWithNominatim(queryString: string, countryCode?: string): Promise<LocationData> {
    try {
        // First, try structured query if we have the address parts
        const addressParts = queryString.split(',').map(part => part.trim());
        
        if (addressParts.length >= 2) {
            const structuredResult = await tryNominatimStructuredQuery(addressParts, countryCode);
            if (!structuredResult.errored) {
                return structuredResult;
            }
        }

        // Fall back to free-form text queries with progressive simplification
        const queries = [
            queryString, // Original full query
            queryString.replace(/,?\s*\d{5}(-\d{4})?$/, '').trim(), // Remove ZIP code only
            queryString.split(',').slice(0, 3).join(',').trim(), // Street + City + State (no ZIP)
            queryString.split(',').slice(0, 2).join(',').trim(), // Just street + city
            queryString.split(',')[0].trim() // Just street address
        ].filter((q, index, arr) => q && arr.indexOf(q) === index); // Remove duplicates and empty strings

        for (let i = 0; i < queries.length; i++) {
            const query = queries[i];
            console.log(`Trying Nominatim free-form query ${i + 1}/${queries.length}:`, query);
            
            const encodedQuery = encodeURIComponent(query);
            const url = `https://nominatim.openstreetmap.org/search?q=${encodedQuery}&format=json&addressdetails=1&limit=1`;
            
            console.log('Nominatim URL:', url);
            
            const response = await fetch(url, {
                headers: {
                    'User-Agent': 'DinePlatform/1.0',
                    'Accept': 'application/json',
                    'Accept-Language': 'en'
                }
            });

            console.log(`Nominatim response status for query ${i + 1}:`, response.status);

            if (!response.ok) {
                console.warn(`Query ${i + 1} failed with status ${response.status}, trying next...`);
                continue;
            }

            const data = await response.json();
            console.log(`Nominatim response data for query ${i + 1}:`, data);
            
            if (data && data.length > 0) {
                const result = data[0];
                console.log('Successfully found location with query:', query);
                console.log('Using nominatim result:', result);
                return {
                    errored: false,
                    latitude: parseFloat(result.lat),
                    longitude: parseFloat(result.lon)
                };
            } else {
                console.warn(`Query ${i + 1} returned empty results, trying next...`);
            }

            // Add a small delay between requests to be respectful
            if (i < queries.length - 1) {
                await new Promise(resolve => setTimeout(resolve, 100));
            }
        }

        // If we get here, all queries failed
        console.warn(`All Nominatim queries failed for: ${queryString}`);
        return {
            errored: true,
            latitude: null,
            longitude: null
        };

    } catch (error: any) {
        console.error("Error fetching nominatim data:", error);
        return {
            errored: true,
            latitude: null,
            longitude: null
        };
    }
}

function buildQueryStringFromAddress(address: Address): string {
    const parts = [];
    
    if (address.line1) parts.push(address.line1.trim());
    if (address.line2) parts.push(address.line2.trim());
    if (address.city) parts.push(address.city.trim());
    if (address.state) parts.push(address.state.trim());
    if (address.postcode) parts.push(address.postcode.trim());
    
    const queryString = parts.filter(part => part.length > 0).join(', ');
    console.log('Built query string:', queryString);
    return queryString;
}

export async function getLocationData(address: Address, countryCode: string): Promise<LocationData | null> {
    console.log('Starting location lookup for:', address, 'Country:', countryCode);
    
    // First attempt - Generic (get with nominatim)
    const queryString = buildQueryStringFromAddress(address);
    let locationData = await getLocationDataWithNominatim(queryString, countryCode);

    if (!locationData.errored) {
        console.log('Successfully got location from Nominatim');
        return locationData;
    }

    console.log('Nominatim failed, trying country-specific APIs...');

    // If nominatim fails, try country-specific APIs
    if (countryCode === "GB") {
        console.log('Trying UK Postcode API...');
        // Attempt 2 - UK Postcode API
        locationData = await getUKLocationDataWithPostcode(address.postcode);

        if (!locationData.errored) {
            console.log('Successfully got location from UK Postcode API');
            return locationData;
        }
    } else if (countryCode === "US") {
        // Skip Census API due to CORS issues in browser
        // Go directly to ZIP code API for US addresses
        console.log('Trying US ZIP Code API...');
        locationData = await getUSLocationDataWithZipCode(address.postcode);

        if (!locationData.errored) {
            console.log('Successfully got location from ZIP Code API');
            return locationData;
        }
    }

    console.log('All location lookup attempts failed');
    // If all attempts fail, return null or a default error response
    return null;
}

// Helper function to convert store address to service address format
export function convertStoreAddressToServiceAddress(storeData: any): Address {
    return {
        line1: storeData.address,
        city: storeData.city,
        state: storeData.state,
        postcode: storeData.zipCode,
        country: storeData.country
    };
}